import { X } from 'lucide-react'
import { DOMAIN_OPTIONS, STAGE_OPTIONS } from '../../constants/aggregator'
import type { FilterState, EntityTags } from '../../hooks/useAggregatorPreferences'
import type { AggregatorProject } from '../../types'

interface FilterPanelProps {
  projects: AggregatorProject[]
  entityTags: EntityTags
  filters: FilterState
  setFilters: (f: FilterState) => void
  onClose: () => void
}

function getStatusBand(p: AggregatorProject): string {
  if (p.discoverable) return 'Discoverable'
  if (p.status === 'archived') return 'Archived'
  return 'Active'
}

type StatusFilterValue = 'discoverable' | 'active' | 'archived'

const STATUS_OPTIONS: { value: StatusFilterValue; label: string }[] = [
  { value: 'discoverable', label: 'Discoverable' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
]

function toggle<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
}

export default function FilterPanel({ projects, entityTags, filters, setFilters, onClose }: FilterPanelProps) {
  // Compute counts
  const domainCounts = new Map<string, number>()
  const stageCounts = new Map<string, number>()
  const statusCounts = new Map<string, number>()
  const entityNames = new Map<string, string>()

  for (const p of projects) {
    const domain = entityTags[p.entity_id]?.domain
    if (domain) domainCounts.set(domain, (domainCounts.get(domain) ?? 0) + 1)

    const stage = entityTags[p.entity_id]?.stage
    if (stage) stageCounts.set(stage, (stageCounts.get(stage) ?? 0) + 1)

    const band = getStatusBand(p).toLowerCase() as StatusFilterValue
    statusCounts.set(band, (statusCounts.get(band) ?? 0) + 1)

    if (!entityNames.has(p.entity_id)) entityNames.set(p.entity_id, p.entity_name)
  }

  const clearAll = () =>
    setFilters({ domains: [], stages: [], statuses: [], entityIds: [] })

  const panelContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h2 className="text-base font-semibold text-gray-900">Filters</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={clearAll}
            className="text-sm text-iflow-600 hover:text-iflow-700 font-medium"
            data-testid="filter-clear-all"
          >
            Clear all
          </button>
          <button
            onClick={onClose}
            aria-label="Close filters"
            className="text-gray-500 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Domain / Vertical filter */}
        <section>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Vertical</h3>
          <div className="space-y-1">
            {DOMAIN_OPTIONS.map((domain) => {
              const count = domainCounts.get(domain) ?? 0
              const checked = filters.domains.includes(domain)
              return (
                <label
                  key={domain}
                  className={`flex items-center gap-2 cursor-pointer text-sm ${count === 0 ? 'text-gray-500' : 'text-gray-700'}`}
                  data-testid={`filter-domain-${domain}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setFilters({ ...filters, domains: toggle(filters.domains, domain) })}
                    className="rounded border-gray-300 text-iflow-500 focus:ring-iflow-400"
                  />
                  <span className="flex-1">{domain}</span>
                  <span className="text-xs text-gray-500">({count})</span>
                </label>
              )
            })}
          </div>
        </section>

        {/* Stage filter */}
        <section>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Stage</h3>
          <div className="space-y-1">
            {STAGE_OPTIONS.map((stage) => {
              const count = stageCounts.get(stage) ?? 0
              const checked = filters.stages.includes(stage)
              return (
                <label
                  key={stage}
                  className={`flex items-center gap-2 cursor-pointer text-sm ${count === 0 ? 'text-gray-500' : 'text-gray-700'}`}
                  data-testid={`filter-stage-${stage}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setFilters({ ...filters, stages: toggle(filters.stages, stage) })}
                    className="rounded border-gray-300 text-iflow-500 focus:ring-iflow-400"
                  />
                  <span className="flex-1">{stage}</span>
                  <span className="text-xs text-gray-500">({count})</span>
                </label>
              )
            })}
          </div>
        </section>

        {/* Status filter */}
        <section>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Status</h3>
          <div className="space-y-1">
            {STATUS_OPTIONS.map(({ value, label }) => {
              const count = statusCounts.get(value) ?? 0
              const checked = filters.statuses.includes(value)
              return (
                <label
                  key={value}
                  className={`flex items-center gap-2 cursor-pointer text-sm ${count === 0 ? 'text-gray-500' : 'text-gray-700'}`}
                  data-testid={`filter-status-${value}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setFilters({ ...filters, statuses: toggle(filters.statuses, value) })}
                    className="rounded border-gray-300 text-iflow-500 focus:ring-iflow-400"
                  />
                  <span className="flex-1">{label}</span>
                  <span className="text-xs text-gray-500">({count})</span>
                </label>
              )
            })}
          </div>
        </section>

        {/* Entity / Inventor filter */}
        <section>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Entity / Inventor</h3>
          <div className="space-y-1">
            {Array.from(entityNames.entries()).map(([entityId, name]) => {
              const checked = filters.entityIds.includes(entityId)
              return (
                <label
                  key={entityId}
                  className="flex items-center gap-2 cursor-pointer text-sm text-gray-700"
                  data-testid={`filter-entity-${entityId}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setFilters({ ...filters, entityIds: toggle(filters.entityIds, entityId) })}
                    className="rounded border-gray-300 text-iflow-500 focus:ring-iflow-400"
                  />
                  <span className="flex-1 truncate">{name}</span>
                </label>
              )
            })}
          </div>
        </section>
      </div>

      {/* Apply button */}
      <div className="px-4 py-3 border-t border-gray-200">
        <button
          onClick={onClose}
          className="w-full py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800"
          data-testid="filter-apply"
        >
          Apply
        </button>
      </div>
    </div>
  )

  return (
    <>
      {/* Desktop sidebar panel */}
      <div
        className="hidden md:flex flex-col w-64 shrink-0 bg-white border border-gray-200 rounded-xl shadow-sm h-fit max-h-[80vh] overflow-hidden"
        data-testid="filter-panel-desktop"
      >
        {panelContent}
      </div>

      {/* Mobile bottom drawer */}
      <div
        className="md:hidden fixed inset-0 z-40"
        data-testid="filter-panel-mobile"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40"
          onClick={onClose}
        />
        {/* Drawer */}
        <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] flex flex-col shadow-xl">
          {panelContent}
        </div>
      </div>
    </>
  )
}
